// ─────────────────────────────────────────────────────────────────────────────
// Desenho de UM frame de spritesheet — fonte ÚNICA compartilhada pelo
// renderizador do jogo (CombatGrid) e pelo preview do painel admin
// (SpriteCalibrator). Se mudar a matemática aqui, muda nos dois.
//
// Layout padrão: grade `cols × rows`. Cada LINHA é uma direção de câmera; cada
// COLUNA um quadro (0 = parado, 1/2 = passada). `rowOrder` remapeia qual
// direção mora em cada linha (sheets fora do padrão). `frameOverrides` ajusta
// escala/desvio de células específicas ("r{lin}c{col}").
// ─────────────────────────────────────────────────────────────────────────────

/** Altura de desenho base do sprite no jogo, antes da escala (px de canvas). */
export const GAME_SPRITE_DRAW_H = 64;

/** Direções na ORDEM PADRÃO das linhas (casa com getDirRow do CombatGrid). */
export const DIR_ORDER = ['SE', 'S', 'SW', 'W', 'NW', 'N', 'NE', 'E'] as const;
export type Dir8 = (typeof DIR_ORDER)[number];

/** Rótulo PT de cada direção, pra nomear as posições no painel. */
export const DIR_LABEL: Record<Dir8, string> = {
  SE: 'Sudeste (baixo-direita)',
  S:  'Sul (baixo)',
  SW: 'Sudoeste (baixo-esquerda)',
  W:  'Oeste (esquerda)',
  NW: 'Noroeste (cima-esquerda)',
  N:  'Norte (cima)',
  NE: 'Nordeste (cima-direita)',
  E:  'Leste (direita)',
};

/** Nome de uma coluna (quadro) pela convenção do jogo. */
export function colLabel(col: number): string {
  if (col === 0) return 'parado';
  if (col === 1) return 'passo A';
  if (col === 2) return 'passo B';
  return `quadro ${col + 1}`;
}

export interface FrameOverride {
  /** escala de DESENHO (tamanho final na tela). */
  scale?: number;
  /** desvio de DESENHO — desloca onde o frame já recortado aparece na tela. */
  offsetX?: number;
  offsetY?: number;
  /**
   * desvio de RECORTE — desloca de onde, dentro do PNG, o frame é fatiado
   * (px do sheet original). Corrige sheets onde o personagem não está
   * verticalmente alinhado igual em cada linha (comum em arte gerada por IA):
   * se a cabeça fica cortada no topo, `srcOffsetY` negativo puxa o recorte
   * pra cima até incluí-la. Nada a ver com `offsetY` (que só move o já cortado).
   */
  srcOffsetX?: number;
  srcOffsetY?: number;
}

export interface SpriteSheetCfg {
  cols: number;
  rows: number;
  /** calibração global (padrão de todas as células) */
  scale: number;
  offsetX: number;
  offsetY: number;
  /** rowOrder[i] = direção da linha i. Vazio = ordem padrão. */
  rowOrder?: string[] | null;
  /** "r{lin}c{col}" → override. */
  frameOverrides?: Record<string, FrameOverride> | null;
}

/**
 * Qual LINHA do sheet contém a direção pedida.
 * @param dirIndex  índice na ordem PADRÃO (0..7) — o que getDirRow devolve
 */
export function resolveSheetRow(dirIndex: number, cfg: SpriteSheetCfg): number {
  const order = cfg.rowOrder && cfg.rowOrder.length ? cfg.rowOrder : DIR_ORDER;
  const want = DIR_ORDER[Math.max(0, Math.min(DIR_ORDER.length - 1, dirIndex))];
  const row = order.indexOf(want);
  const resolved = row >= 0 ? row : dirIndex;
  return Math.max(0, Math.min(cfg.rows - 1, resolved));
}

/**
 * Calibração efetiva de uma célula = global + override da célula.
 * `srcOffsetX/Y` não têm "global" — são específicos de cada sheet, default 0.
 */
export function effectiveCell(cfg: SpriteSheetCfg, row: number, col: number): Required<FrameOverride> {
  const o = cfg.frameOverrides?.[`r${row}c${col}`] ?? {};
  return {
    scale:      o.scale      ?? cfg.scale,
    offsetX:    o.offsetX    ?? cfg.offsetX,
    offsetY:    o.offsetY    ?? cfg.offsetY,
    srcOffsetX: o.srcOffsetX ?? 0,
    srcOffsetY: o.srcOffsetY ?? 0,
  };
}

export interface DrawFrameOpts {
  /** direção desejada (índice na ordem padrão 0..7). */
  dirIndex: number;
  /** coluna/quadro a desenhar. */
  col: number;
  /** ponto onde os PÉS tocam o chão, em px de canvas. */
  groundX: number;
  groundY: number;
  /** deslocamento do "pulinho" da passada (px, subtrai de Y). */
  bob?: number;
  /** altura de desenho base antes da escala (default 64). */
  baseDrawH?: number;
}

export interface DrawFrameResult {
  /** largura desenhada — útil pra anel de seleção / barra de HP. */
  drawW: number;
  drawX: number;
  drawY: number;
  /** linha e calibração usadas (debug / preview). */
  row: number;
  cell: Required<FrameOverride>;
}

/**
 * Recorta e desenha o frame no contexto. Retorna a geometria pra quem precisa
 * posicionar decorações (anel, barra, nome) por cima.
 */
export function drawSpriteFrame(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource & { width: number; height: number },
  cfg: SpriteSheetCfg,
  opts: DrawFrameOpts,
): DrawFrameResult {
  const cols = Math.max(1, cfg.cols);
  const rows = Math.max(1, cfg.rows);
  const baseH = opts.baseDrawH ?? 64;
  const bob = opts.bob ?? 0;

  const row = resolveSheetRow(opts.dirIndex, cfg);
  const col = Math.max(0, Math.min(cols - 1, opts.col));
  const cell = effectiveCell(cfg, row, col);

  const frameW = img.width / cols;
  const frameH = img.height / rows;
  const drawH = baseH * cell.scale;
  const drawW = Math.round(drawH * (frameW / frameH));

  const drawX = opts.groundX - drawW / 2 + cell.offsetX;
  const drawY = opts.groundY - drawH - bob + cell.offsetY;

  // Recorte: linha/coluna padrão + desvio de RECORTE, sempre preso dentro do
  // PNG (nunca amostra fora da imagem, mesmo com um srcOffset exagerado).
  const srcX = clamp(col * frameW + cell.srcOffsetX, 0, Math.max(0, img.width - frameW));
  const srcY = clamp(row * frameH + cell.srcOffsetY, 0, Math.max(0, img.height - frameH));

  ctx.drawImage(img, srcX, srcY, frameW, frameH, drawX, drawY, drawW, drawH);

  return { drawW, drawX, drawY, row, cell };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
